import type { TableCell } from './statement-table-reader';

export function normalizeHeader(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/[^\p{L}\p{N} ]/gu, '')
    .trim();
}

export function normalizeCell(value: TableCell): string {
  if (value === null || value === undefined || value === false) {
    return '';
  }
  return String(value)
    .replace(/[\n\r]+/g, ' ')
    .trim();
}

export function excelSerialToDate(serial: number): Date | null {
  if (!Number.isFinite(serial)) {
    return null;
  }

  const wholeDays = Math.floor(serial);
  const fractionalDay = serial - wholeDays;
  // Excel's 1900 date system includes the non-existent 1900-02-29,
  // so serials after that point need to skip the phantom day.
  const adjustedDays = wholeDays > 59 ? wholeDays - 1 : wholeDays;
  // Use Date.UTC to produce a UTC midnight date so that repository
  // normalisation (setUTCHours(0,0,0,0)) never shifts the calendar day.
  const date = new Date(Date.UTC(1899, 11, 31 + adjustedDays));

  if (fractionalDay !== 0) {
    date.setTime(date.getTime() + Math.round(fractionalDay * 24 * 60 * 60 * 1000));
  }

  return Number.isNaN(date.getTime()) ? null : date;
}

export function parseDateValue(value: TableCell): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return excelSerialToDate(value);
  }

  const normalized = normalizeCell(value);
  if (!normalized) return null;

  const dotMatch = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (dotMatch) {
    const [, day, month, year, hours = '0', minutes = '0'] = dotMatch;
    const fullYear = year.length === 2 ? `20${year}` : year;
    // Use Date.UTC to produce a UTC midnight date so that repository
    // normalisation (setUTCHours(0,0,0,0)) never shifts the calendar day.
    return new Date(Date.UTC(Number(fullYear), Number(month) - 1, Number(day), Number(hours), Number(minutes)));
  }

  const isoDate = new Date(normalized);
  return Number.isNaN(isoDate.getTime()) ? null : isoDate;
}

export function parseAmount(value: TableCell): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const raw = normalizeCell(value);
  if (!raw) return null;

  const negative = raw.includes('(') && raw.includes(')');
  let normalized = raw.replace(/[^\d,.-]/g, '');

  if (normalized.includes(',') && normalized.includes('.')) {
    normalized =
      normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
        ? normalized.replace(/\./g, '').replace(',', '.')
        : normalized.replace(/,/g, '');
  } else if (normalized.includes(',')) {
    normalized = normalizeSingleSeparatorAmount(normalized, ',');
  } else if (normalized.includes('.')) {
    normalized = normalizeSingleSeparatorAmount(normalized, '.');
  }

  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return negative ? -Math.abs(parsed) : parsed;
}

function normalizeSingleSeparatorAmount(value: string, separator: ',' | '.'): string {
  const parts = value.split(separator);
  if (parts.length === 1) {
    return value;
  }

  const trailingGroups = parts.slice(1);
  if (trailingGroups.every((part) => /^\d{3}$/.test(part))) {
    return parts.join('');
  }

  return separator === ',' ? value.replace(',', '.') : value;
}

export function buildExternalId(input: {
  date: Date;
  amount: number;
  currencyCode: string;
  description: string;
  reference: string | null;
}): string {
  const ref = input.reference ? normalizeHeader(input.reference) : 'no-ref';
  return [
    input.date.toISOString(),
    input.amount.toFixed(2),
    input.currencyCode,
    normalizeHeader(input.description),
    ref,
  ].join('|');
}
